'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { Search } from 'lucide-react';
import {
  getBannersForCurrentPage,
  getMixedJobsAndBanners,
} from '@/lib/utils/jobs';
import Pagination from '@/components/ui/PaginationComponent';
import InputString from '@/components/ui/InputString';
import Image from 'next/image';
import Button from '@/components/ui/Button';
import AdvancedSearch from '@/components/ui/AdvancedSearch';
import SearchTags from '@/features/jobs/components/SearchTags';
import NoDataCard from '@/components/ui/NoDataCard';
import GuestLimitNotice from '@/features/jobs/components/GuestLimitNotice';
import HomeBanner from '@/features/home/components/Banner';
import JobNavigationTab, {
  JobTabType,
} from '@/features/home/components/JobNavigationTab';
import JobListItem from '@/features/jobs/components/JobListItem';
import { JobListSkeleton } from '@/features/jobs/components/JobListItemSkeleton';
import {
  useBannerJobClickCount,
  useBannerJobList,
  useSearchJobs,
} from '@/features/jobs';
import { IListResponse } from '@/shared';
import {
  BannerAdsHistoryDto,
  JobListFilterDto,
  PublicJobListDTO,
} from '@/shared/job/job.public.dto';

interface Props {
  showAdvancedSearchBtn?: boolean;
}

function unwrapJobList(
  payload: IListResponse<PublicJobListDTO> | PublicJobListDTO[] | undefined
): { jobs: PublicJobListDTO[]; totalPages: number } {
  if (Array.isArray(payload)) {
    return { jobs: payload, totalPages: 1 };
  }
  if (Array.isArray(payload?.data)) {
    return { jobs: payload?.data ?? [], totalPages: payload?.totalPages ?? 0 };
  }
  const nested = payload?.data as IListResponse<PublicJobListDTO> | undefined;
  if (Array.isArray(nested?.data)) {
    return { jobs: nested?.data ?? [], totalPages: nested?.totalPages ?? 0 };
  }
  return { jobs: [], totalPages: 0 };
}

function shuffleInPlace<T>(items: T[]): T[] {
  const copy = [...items];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

function getUrlFilters(
  params: URLSearchParams
): Record<string, string> {
  const filters: Record<string, string> = {};
  params.forEach((value, key) => {
    if (key === 'q') {
      filters.search = value;
      return;
    }
    if (key === 'page' || key === 'type') return;
    filters[key] = value;
  });
  return filters;
}

const JobsMainList = ({ showAdvancedSearchBtn = false }: Props) => {
  const searchParams = useSearchParams();
  const router = useRouter();
  const targetRef = useRef<HTMLDivElement>(null);

  const urlPage = Number(searchParams.get('page')) || 1;
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [showModal, setShowModal] = useState(false);
  const [tabType, setTabType] = useState<JobTabType>(() => {
    const type = searchParams.get('type');
    return type === 'cv' ||
      type === 'view' ||
      type === 'share' ||
      type === 'featured'
      ? type
      : 'all';
  });
  const [page, setPage] = useState(urlPage);
  const [urlFilters, setUrlFilters] = useState(() =>
    getUrlFilters(searchParams)
  );
  const searchKey = searchParams.toString();

  const jobSearchParams = useMemo(
    () =>
      ({
        type: tabType,
        page,
        ...urlFilters,
      }) as JobListFilterDto,
    [tabType, page, urlFilters]
  );

  const filterKey = useMemo(() => {
    const filterParams = new URLSearchParams(searchKey);
    filterParams.delete('page');
    filterParams.set('type', tabType);
    return filterParams.toString();
  }, [searchKey, tabType]);

  const urlFilterKey = useMemo(() => {
    const filterParams = new URLSearchParams(searchKey);
    filterParams.delete('page');
    filterParams.delete('type');
    return filterParams.toString();
  }, [searchKey]);

  const handleTabChange = useCallback(
    (value: JobTabType) => {
      setTabType(value);
      setPage(1);
      setSearchQuery('');
      setUrlFilters({});
      if (searchKey) {
        router.replace('/job/list', { scroll: false });
      }
    },
    [router, searchKey]
  );

  useEffect(() => {
    const next = getUrlFilters(new URLSearchParams(searchKey));
    setUrlFilters((prev) =>
      JSON.stringify(prev) === JSON.stringify(next) ? prev : next
    );
  }, [searchKey]);

  useEffect(() => {
    setPage(urlPage);
  }, [urlFilterKey, urlPage]);

  const { data: bannerJobs } = useBannerJobList();
  const {
    data: jobsData,
    isLoading,
    guestLimitMessage,
  } = useSearchJobs(jobSearchParams);
  const { mutate: setBannerJobClickCount } = useBannerJobClickCount();

  const banners = Array.isArray(bannerJobs)
    ? bannerJobs
    : (bannerJobs?.data ?? []);
  const { jobs, totalPages } = unwrapJobList(jobsData);

  const shuffledBanners = useMemo(() => {
    if (!banners.length) return banners;
    return shuffleInPlace(banners);
  }, [banners]);

  const paginate = useCallback(
    (pageNumber: number) => {
      setPage(pageNumber);
      sessionStorage.removeItem('jobs-scroll');
      const params = new URLSearchParams(searchParams.toString());
      params.set('page', pageNumber.toString());
      router.push(`/job/list?${params.toString()}`, { scroll: false });
      window.scrollTo({ top: 0, behavior: 'instant' });
    },
    [router, searchParams]
  );

  const handleSearch = useCallback(() => {
    router.push(`/job/list?q=${searchQuery}`, { scroll: false });
  }, [router, searchQuery]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleSearch();
      }
    },
    [handleSearch]
  );

  const handleBannerJobClick = useCallback(
    (id: number) => {
      setBannerJobClickCount(id);
    },
    [setBannerJobClickCount]
  );

  const renderBanner = useCallback(
    (banner: BannerAdsHistoryDto) => (
      <HomeBanner
        onBannerJobClick={handleBannerJobClick}
        bannerId={banner.id}
        companyId={banner.companyId}
        companyLogo={banner.logo}
        companyName={banner.companyName}
        showJobCount={true}
        jobCount={banner.jobCount}
        location={banner.locationName}
        companyLink={banner.alias}
        address={banner.locationName}
      />
    ),
    [handleBannerJobClick]
  );

  const mixedContent = useMemo(() => {
    const bannersToShow = getBannersForCurrentPage(shuffledBanners ?? [], page);
    return getMixedJobsAndBanners(
      jobs,
      shuffledBanners ?? [],
      bannersToShow,
      filterKey,
      page
    );
  }, [shuffledBanners, page, filterKey, jobs]);

  useEffect(() => {
    if (jobs.length === 0) return;
    const savedScroll = sessionStorage?.getItem('jobs-scroll');
    if (savedScroll) {
      const timeoutId = setTimeout(() => {
        const rafId = requestAnimationFrame(() => {
          window.scrollTo({ top: parseInt(savedScroll), behavior: 'instant' });
          sessionStorage.removeItem('jobs-scroll');
        });
        return () => cancelAnimationFrame(rafId);
      }, 50);
      return () => clearTimeout(timeoutId);
    }
  }, [jobs]);

  return (
    <div className="rounded-xl bg-white w-full" ref={targetRef}>
      <div className="font-medium flex justify-between items-center">
        <JobNavigationTab tabType={tabType} onTabChange={handleTabChange} />
        {showAdvancedSearchBtn && (
          <div>
            <Button
              className="w-8 h-8 mr-2 p-2"
              variant="primary"
              onClick={() => setShowModal(true)}
            >
              <Image
                src="/images/icons/filter-white.svg"
                alt="filter"
                width={32}
                height={32}
                className="w-full h-full"
              />
            </Button>
            <AdvancedSearch
              isOpen={showModal}
              onClose={() => setShowModal(false)}
            />
          </div>
        )}
      </div>

      {showAdvancedSearchBtn && (
        <div className="w-full p-2 border-t border-black/10">
          <InputString
            name="search"
            placeholder="Та ямар ажил хайж байна вэ?"
            button={Search}
            className="h-11 w-full border-[1.5px] border-primary rounded-lg"
            onKeyDown={handleKeyDown}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onButtonClick={handleSearch}
          />
        </div>
      )}

      <SearchTags />

      {guestLimitMessage ? (
        <GuestLimitNotice message={guestLimitMessage} />
      ) : isLoading ? (
        <JobListSkeleton count={6} />
      ) : jobs.length > 0 ? (
        <>
          <div>
            {mixedContent.map((item) =>
              item.type === 'banner' ? (
                <div key={item.key}>{renderBanner(item.data)}</div>
              ) : (
                <JobListItem key={item.key} job={item.data} />
              )
            )}
          </div>
          {totalPages > 0 && (
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              onPageChange={paginate}
            />
          )}
        </>
      ) : (
        <NoDataCard text="Илэрц олдсонгүй" image="/images/no-data.svg" />
      )}
    </div>
  );
};

export default JobsMainList;
